import Timestampable from '@/models/Timestampable'
import {
  LogItemInterface,
  LogItemDocInterface,
  id,
  dateOnly,
} from '@/types/models'
import {
  db,
  auth,
  serverTimestamp,
  firestoreTimestampToDate,
  DocumentReference,
} from '@/firebase'
import store from '@/store'
import Plant from '@/models/Plant'
import Harvest from '@/models/Harvest'
import Garden from '@/models/Garden'
import Image from '@/models/Image'
import { format } from 'date-fns'
import {
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore'

export enum LogItemCategory {
  Harvest = 'harvest',
  Image = 'image',
  Timeline = 'timeline',
  Action = 'action',
  Milestone = 'milestone',
  Water = 'water',
  Comment = 'comment',
}

export default class LogItem extends Timestampable {
  isNew = false

  id: id
  gardenId: id
  date: dateOnly
  category: LogItemCategory
  type: string
  note?: string | null

  // batchId?: string | null;
  // plantIds: id[];
  refs: string[]

  imageGetter: (li: LogItem) => Image[] = (li) =>
    store.getters['gardens/images/logItemImages'](li.gardenId, li.id)

  collections() {
    return {}
  }

  constructor(data: LogItemInterface) {
    super(data)

    // this.plantId = data.plantId
    this.id = data.id
    this.gardenId = data.gardenId
    this.date = data.date
    this.category = data.category
    this.type = data.type
    this.note = data.note

    // this.batchId = data.batchId
    // this.plantIds = data.plantIds
    this.refs = data.refs

    this.isNew = data.isNew || false
  }

  get garden(): Garden {
    return store.getters['gardens/find'](this.gardenId)
  }

  get plantIds(): id[] {
    return this.refs
      .map((ref: string) => ref.split('/'))
      .filter(
        (refArr: Array<string>) =>
          refArr.length >= 4 &&
          refArr[0] === 'gardens' &&
          refArr[2] === 'plants'
      )
      .map((refArr: Array<string>) => refArr[3])
  }

  get plants(): Plant[] {
    return store.getters['gardens/plants/filter'](this.plantIds)
  }

  get images(): Image[] {
    // console.log('[LogItem.images]', this.category, this.type, this.imageGetter(this), this.imageGetter)
    return this.imageGetter(this)
  }

  static fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
    gardenId: id
  ): LogItem {
    const data = snapshot.data(options)

    const result: LogItemInterface = {
      id: snapshot.id,
      gardenId: gardenId,
      date: data.date,
      category: data.category,
      type: data.type,
      note: data.note,

      // batchId: data.batchId,
      // plantIds: data.plantIds,
      // refs: data.refs.map((r: DocumentReference<LogItem>) => r.path),
      // refs: data.refs.map((r: DocumentReference<LogItem>) => r.path),
      refs: data.refs.map((r: DocumentReference) => r.path),

      createdBy: data.createdBy,
      createdAt: firestoreTimestampToDate(data.createdAt),
      updatedBy: data.updatedBy,
      updatedAt: firestoreTimestampToDate(data.updatedAt),

      isNew: false,
    }

    return new LogItem(result)
  }

  static fromHarvest = (h: Harvest) => {
    const li = new LogItem({
      id: `harvest-${h.id}`,

      gardenId: h.plant.gardenId,
      date: h.date,
      category: LogItemCategory.Harvest,
      type: `${h.quantity}`,
      note: [
        { key: 'Quality', value: h.quality },
        { key: 'Ripeness', value: h.ripeness },
        { key: 'Size', value: h.size },
        { key: 'Traits', value: h.traits.join(', ') },
        { key: 'Note', value: h.note },
      ]
        .filter((z) => !!z.value)
        .map((z) => `${z.key}: ${z.value}`)
        .join(' | '),
      refs: [`gardens/${h.plant.gardenId}/plants/${h.plantId}`],
    })

    li.imageGetter = () =>
      // store.getters['gardens/plants/harvests/logItemImages'](li.gardenId, li.id)
      store.getters['gardens/images/plantHarvestImages'](
        h.plant.gardenId,
        h.plantId,
        h.id
      )

    return li
  }

  static fromImage = (i: Image) =>
    new LogItem({
      id: `image-${i.id}`,

      gardenId: i.gardenId,
      date: format(i.takenAt || i.createdAt || new Date(), 'yyyy-MM-dd'),
      category: LogItemCategory.Image,
      type: i.id, // i.mediumUrl,
      note: i.note, // [`Quality: ${h.quality}`, `Ripeness: ${h.ripeness}`, `Size: ${h.size}`, `Traits: ${h.traits.join(', ')}`, `Note: ${h.note}`].join(' | '),
      refs: i.refs, // [`plants/${h.plantId}`],
    })

  static firestoreConverter = (gardenId: id) => ({
    toFirestore(logItem: LogItem): DocumentData {
      const h: LogItemDocInterface = {
        date: logItem.date,
        category: logItem.category,
        type: logItem.type,
        note: logItem.note || null,

        // batchId: logItem.batchId || null,
        // plantIds: logItem.plantIds,
        // refs: logItem.refs,
        refs: logItem.refs.map((ref: string) => db.doc(ref)),
      }

      if (logItem.isNew) {
        h.createdBy = auth.currentUser()?.uid
        h.createdAt = serverTimestamp()
      } else {
        h.updatedBy = auth.currentUser()?.uid
        h.updatedAt = serverTimestamp()
      }

      // console.log('LogItem.toFirestore', h)

      return h
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot,
      options: SnapshotOptions
    ): LogItem {
      return LogItem.fromFirestore(snapshot, options, gardenId)
    },
  })
}
